import React from 'react';
import styled from 'styled-components';
import useInterval from '../hooks/useInterval';

import CardSlider from '../components/CardSliderDemo';
import Showcase from '../components/ShowcaseDemo';
import Header from '../components/Header';
import FeaturedEventsScreen from '../components/FeaturedEventsDemo';

import { colors, padding, fonts } from '../GlobalStyle';

import { getTestEventTimes, getTestEventData } from '../data/data';
import { calc4k } from '../utilities';

import image0 from '../data/images/image0.jpeg';
import image1 from '../data/images/image1.jpeg';
import image2 from '../data/images/image2.jpeg';
import image3 from '../data/images/image3.jpeg';
import image4 from '../data/images/image4.jpeg';
import image5 from '../data/images/image5.jpeg';

import image0Featured from '../data/images/featured_image_0.jpeg';
import image1Featured from '../data/images/featured_image_1.jpeg';
import image2Featured from '../data/images/featured_image_2.jpeg';

import image0Thumb from '../data/images/image0_thumb.jpeg';
import image1Thumb from '../data/images/image1_thumb.jpeg';
import image2Thumb from '../data/images/image2_thumb.jpeg';
import image3Thumb from '../data/images/image3_thumb.jpeg';
import image4Thumb from '../data/images/image4_thumb.jpeg';
import image5Thumb from '../data/images/image5_thumb.jpeg';

const TEST_IMAGES = [image0, image1, image2, image3, image4, image5];
const TEST_IMAGES_FEATURED = [image0Featured, image1Featured, image2Featured];
const TEST_IMAGES_THUMB = [
    image0Thumb,
    image1Thumb,
    image2Thumb,
    image3Thumb,
    image4Thumb,
    image5Thumb
];

export default function EventsNew() {
    const [isLoaded, setIsLoaded] = React.useState(false);
    const [alertActive, setAlertActive] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState(null);
    const [currStep, setCurrStep] = React.useState(0);
    const [currEventIdx, setCurrEventIdx] = React.useState(null);
    const [lastEvent, setLastEvent] = React.useState(false);
    const [eventsFinished, setEventsFinished] = React.useState(false);

    // set up test data
    const testShowcaseImages = TEST_IMAGES.slice().reverse();
    const testEventTimes = getTestEventTimes(15);
    const testEventData = getTestEventData();
    const testSliderData = getTestSliderData();

    function getTestSliderData() {
        const images = TEST_IMAGES_THUMB.slice(1);
        const data = testEventData.slice(1);

        return data.map((d, idx) => ({ ...d, img: images[idx] }));
    }

    // determine closest test time when component mounts
    React.useEffect(() => {
        const now = new Date().getTime();
        const closestEventTimeIdx = testEventTimes.findIndex(
            e => e.getTime() > now
        );
        setCurrEventIdx(closestEventTimeIdx - 1);
        setTimeout(() => {
            setIsLoaded(true);
        }, 2000);
    }, []);

    // control test data
    React.useEffect(() => {
        if (currStep === 5) {
            setInterval(() => {
                setLastEvent(true);
            }, 2000);
        }

        if (currStep === 3) {
            setAlertActive(true);
            setAlertMessage(
                `Please keep in mind that the museum will close at 4pm today for private event`
            );
        }

        if (currStep === 5) {
            setAlertActive(false);
        }

        if (currStep === 7) {
            setAlertMessage(null);
        }

        if (currStep === 10) {
            setEventsFinished(true);
        }

        if (currStep === 27) {
            reset();
        }
    }, [currStep]);

    function reset() {
        window.location.reload();
    }

    // query for the next event every second
    useInterval(() => {
        const now = new Date();
        const nextEvent = testEventTimes[currEventIdx + 1];
        const needNextEvent = nextEvent && now > nextEvent.getTime();

        if (needNextEvent) {
            if (currStep < 5) {
                setCurrEventIdx(s => s + 1);
            }

            setCurrStep(s => s + 1);
        }
    }, 1000);

    const currEvent =
        testEventData[
            currStep < testEventData.length
                ? currStep
                : testEventData.length - 1
        ];

    return (
        <LoadingScreen isLoaded={isLoaded}>
            <LoadingText isLoaded={isLoaded}>Loading...</LoadingText>
            <Container isLoaded={isLoaded}>
                <Header
                    title="Today's Events"
                    position={eventsFinished && 'relative'}
                    alertActive={alertActive}
                    alertMessage={alertMessage}
                />
                {eventsFinished ? (
                    <FeaturedEventsScreen />
                ) : (
                    <Events>
                        <Showcase
                            images={testShowcaseImages}
                            currEvent={currEvent}
                            currStep={currStep}
                        />
                        <CardSliderContainer>
                            <CardSlider
                                data={testSliderData}
                                currStep={currStep}
                            />
                            <NoEventsText>
                                {lastEvent && `No more events for today`}
                            </NoEventsText>
                        </CardSliderContainer>
                        <SingleFeaturedEvent>
                            <SingleFeaturedEventHeader>
                                <SingleFeaturedEventHeaderLeft>
                                    Coming Up
                                </SingleFeaturedEventHeaderLeft>
                                <SingleFeaturedEventHeaderRight>
                                    Friday, May 31 1-4 PM
                                </SingleFeaturedEventHeaderRight>
                            </SingleFeaturedEventHeader>
                            <SingleFeaturedEventTitle>
                                33rd Annual Japanese New Year's Bell-Ringing
                                Ceremony
                            </SingleFeaturedEventTitle>
                        </SingleFeaturedEvent>
                    </Events>
                )}
            </Container>
        </LoadingScreen>
    );
}

const LoadingScreen = styled.div`
    visibility: ${p => (p.isLoaded ? 'hidden' : 'visible')};
`;

const LoadingText = styled.h1`
    display: ${p => (p.isLoaded ? 'none' : 'block')};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const Container = styled.div`
    visibility: ${p => (p.isLoaded ? 'visible' : 'hidden')};
`;

const NoEventsText = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: ${calc4k(50)};
    font-family: ${fonts.headline};
    transition: opacity 600ms ease-in-out;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 1;
`;

const Events = styled.div`
    flex-direction: column;
`;

const SingleFeaturedEvent = styled.div`
    border-top: ${calc4k(1)} solid #383736;
    display: flex;
    flex-direction: column;
    height: ${calc4k(782)};
    padding: 0 ${padding.side};
`;

const SingleFeaturedEventHeader = styled.div`
    margin-top: ${calc4k(70)};
    display: flex;
    justify-content: space-between;
`;

const SingleFeaturedEventHeaderLeft = styled.div`
    font-family: ${fonts.headline};
    text-transform: uppercase;
    color: #afafaf;
    font-size: ${calc4k(50)};
    font-weight: 500;
`;

const SingleFeaturedEventHeaderRight = styled.div`
    font-family: ${fonts.headline};
    font-size: ${calc4k(50)};
    font-weight: 500;
`;

const SingleFeaturedEventTitle = styled.div`
    font-family: ${fonts.headline};
    margin-top: ${calc4k(180)};
    font-size: ${calc4k(90)};
    line-height: ${calc4k(140)};
    font-weight: 500;
`;

const CardSliderContainer = styled.div`
    position: relative;
`;
